import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Building2, Plus, Pencil, Power, Upload, Trash2, FileSpreadsheet, Eye, X, Download, Zap, BedDouble, FolderOpen, BookOpen, QrCode, Copy, Check } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fmtDateIST } from '../lib/tz';


export default function AdminOutlets() {
  const navigate = useNavigate();
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', city: '', outlet_type: '', portfolio_id: '', target_fc_pct: '', target_bc_pct: '', hierarchy: 'primary', parent_outlet_id: '', include_in_totals: true, legal_entity_name: '', petty_cash_max_amount: '', accounts_email: '', accounts_whatsapp: '', pos_type: 'none' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [excelUploading, setExcelUploading] = useState('');
  const [planningDialog, setPlanningDialog] = useState({ open: false, outlet: null, data: null, loading: false });
  const [rateDialog, setRateDialog] = useState({ open: false, outlet: null, loading: false });
  const [portfolios, setPortfolios] = useState([]);
  const [rateForm, setRateForm] = useState({
    electricity: { unit_price: '', unit: 'kWh' },
    gas: { unit_price: '', unit: 'm3' },
    water: { unit_price: '', unit: 'kL' }
  });
  const [rateSaving, setRateSaving] = useState(false);
  const [budgetUploading, setBudgetUploading] = useState('');
  const [budgetYear, setBudgetYear] = useState(new Date().getFullYear());
  const [budgetDialog, setBudgetDialog] = useState({ open: false, outlet: null, data: null, loading: false });
  const [hubDialog, setHubDialog] = useState({ open: false, outlet: null, data: null, loading: false });
  const [hubCopied, setHubCopied] = useState(false);
  const [hubFeedback, setHubFeedback] = useState({ selected: null, options: [], saving: false });

  // Business Unit licensing status. Legacy tenants get
  // `licensing_enabled=false` → the banner disappears and Add Outlet
  // behaves exactly as before.
  const [buStatus, setBuStatus] = useState(null);
  const [activateOpen, setActivateOpen] = useState(false);
  const [activateCode, setActivateCode] = useState('');
  const [activateBusy, setActivateBusy] = useState(false);

  const loadBuStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/entitlements/business-units`,
                                 { headers: getHeaders() });
      setBuStatus(r.data);
    } catch {
      setBuStatus(null);   // treat as unlimited on failure — never block UI
    }
  }, [API, getHeaders]);

  useEffect(() => { loadBuStatus(); }, [loadBuStatus]);

  const redeemActivationCode = async () => {
    if (!activateCode.trim()) { toast.error('Enter an activation code'); return; }
    setActivateBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/entitlements/business-units/redeem`,
        { activation_code: activateCode.trim() },
        { headers: getHeaders() },
      );
      toast.success(`+${r.data.units_added} Business Unit(s) activated`);
      setActivateOpen(false);
      setActivateCode('');
      loadBuStatus();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Activation failed'));
    } finally { setActivateBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [outRes, pfRes] = await Promise.all([
        axios.get(`${API}/admin/outlets`, { headers: getHeaders() }),
        axios.get(`${API}/admin/portfolios`, { headers: getHeaders() })
      ]);
      setOutlets(outRes.data);
      setPortfolios(pfRes.data);
    } catch {
      toast.error('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    const defaultPf = portfolios.find(p => p.is_default);
    setForm({ name: '', city: '', outlet_type: '', portfolio_id: defaultPf?.id || '', target_fc_pct: '', target_bc_pct: '', hierarchy: 'primary', parent_outlet_id: '', include_in_totals: true, legal_entity_name: '', petty_cash_max_amount: '', accounts_email: '', accounts_whatsapp: '', pos_type: 'none' });
    setDialogOpen(true);
  };

  const openEdit = (outlet) => {
    setEditing(outlet);
    setForm({
      name: outlet.name,
      city: outlet.city || '',
      outlet_type: outlet.type || '',
      portfolio_id: outlet.portfolio_id || '',
      target_fc_pct: outlet.target_fc_pct != null ? String(outlet.target_fc_pct) : '',
      target_bc_pct: outlet.target_bc_pct != null ? String(outlet.target_bc_pct) : '',
      hierarchy: outlet.hierarchy === 'sub' ? 'sub' : 'primary',
      parent_outlet_id: outlet.parent_outlet_id || '',
      include_in_totals: outlet.include_in_totals !== false,
      legal_entity_name: outlet.legal_entity_name || '',
      petty_cash_max_amount: outlet.petty_cash_max_amount != null ? String(outlet.petty_cash_max_amount) : '',
      accounts_email: outlet.accounts_email || '',
      accounts_whatsapp: outlet.accounts_whatsapp || '',
      pos_type: outlet.pos_type || 'none',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Outlet name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        city: form.city,
        outlet_type: form.outlet_type,
        portfolio_id: form.portfolio_id,
        target_fc_pct: form.target_fc_pct === '' ? null : Number(form.target_fc_pct),
        target_bc_pct: form.target_bc_pct === '' ? null : Number(form.target_bc_pct),
        hierarchy: form.hierarchy === 'sub' ? 'sub' : 'primary',
        parent_outlet_id: form.hierarchy === 'sub' ? (form.parent_outlet_id || null) : null,
        include_in_totals: !!form.include_in_totals,
        legal_entity_name: form.legal_entity_name.trim(),
        petty_cash_max_amount: form.petty_cash_max_amount === '' ? null : Number(form.petty_cash_max_amount),
        accounts_email: form.accounts_email.trim(),
        accounts_whatsapp: form.accounts_whatsapp.trim(),
        pos_type: form.pos_type || 'none',
      };
      if (editing) {
        await axios.put(`${API}/admin/outlets/${editing.id}`, payload, { headers: getHeaders() });
        toast.success('Outlet updated');
      } else {
        // Create endpoint accepts a subset; targets are admin-set later via Edit.
        await axios.post(`${API}/admin/outlets`, payload, { headers: getHeaders() });
        toast.success('Outlet created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'BUSINESS_UNIT_LIMIT_REACHED') {
        toast.error(detail.message || 'Business Unit limit reached');
        setActivateOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save outlet'));
      }
    } finally {
      setSaving(false);
      loadBuStatus();
    }
  };

  const toggleStatus = async (outlet) => {
    try {
      const res = await axios.patch(`${API}/admin/outlets/${outlet.id}/status`, {}, { headers: getHeaders() });
      toast.success(`Outlet ${res.data.status === 'active' ? 'activated' : 'deactivated'}`);
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const toggleRooms = async (outlet) => {
    try {
      const res = await axios.patch(`${API}/admin/outlets/${outlet.id}/rooms`, {}, { headers: getHeaders() });
      toast.success(`Rooms ${res.data.rooms_enabled ? 'enabled' : 'disabled'} for ${outlet.name}`);
      load();
    } catch {
      toast.error('Failed to toggle rooms');
    }
  };

  const handleLogoUpload = async (outlet, file) => {
    if (!file) return;
    setUploading(outlet.id);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/admin/outlets/${outlet.id}/logo`, formData, {
        headers: getHeaders()
      });
      toast.success('Logo uploaded');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally { setUploading(''); }
  };

  const handleLogoDelete = async (outlet) => {
    try {
      await axios.delete(`${API}/admin/outlets/${outlet.id}/logo`, { headers: getHeaders() });
      toast.success('Logo removed');
      load();
    } catch { toast.error('Failed to remove logo'); }
  };

  const handleExcelUpload = async (outlet, file) => {
    if (!file) return;
    setExcelUploading(outlet.id);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/outlets/${outlet.id}/upload-planning`, formData, {
        headers: getHeaders()
      });
      const d = res.data;
      toast.success(`Uploaded ${d.total_records} records (${d.inserted} new, ${d.updated} updated)`);
      if (d.errors?.length > 0) toast.warning(`${d.errors.length} row(s) had issues`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload Excel');
    } finally { setExcelUploading(''); }
  };
  const openHubDialog = async (outlet) => {
    setHubDialog({ open: true, outlet, data: null, loading: true });
    setHubCopied(false);
    setHubFeedback({ selected: null, options: [], saving: false });
    try {
      const [linkRes, fbRes] = await Promise.all([
        axios.get(`${API}/admin/outlets/${outlet.id}/hub-link`, { headers: getHeaders() }),
        axios.get(`${API}/admin/outlets/${outlet.id}/hub-feedback`, { headers: getHeaders() }),
      ]);
      setHubDialog(p => ({ ...p, data: linkRes.data, loading: false }));
      setHubFeedback({ selected: fbRes.data.selected, options: fbRes.data.options || [], saving: false });
    } catch {
      toast.error('Could not load NFC hub link');
      setHubDialog(p => ({ ...p, loading: false }));
    }
  };

  const saveHubFeedback = async (nextValue) => {
    if (!hubDialog.outlet) return;
    setHubFeedback((p) => ({ ...p, saving: true }));
    try {
      const res = await axios.put(
        `${API}/admin/outlets/${hubDialog.outlet.id}/hub-feedback`,
        { form_type: nextValue },
        { headers: getHeaders() },
      );
      setHubFeedback((p) => ({ ...p, selected: res.data.selected, saving: false }));
      toast.success(nextValue ? 'Feedback form linked' : 'Feedback card hidden');
    } catch {
      toast.error('Could not update feedback selection');
      setHubFeedback((p) => ({ ...p, saving: false }));
    }
  };

  const copyHubLink = async () => {
    if (!hubDialog.data?.url) return;
    try {
      await navigator.clipboard.writeText(hubDialog.data.url);
      setHubCopied(true);
      toast.success('Link copied');
      setTimeout(() => setHubCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  const downloadHubQr = () => {
    if (!hubDialog.data?.qr_png_data_url) return;
    const a = document.createElement('a');
    a.href = hubDialog.data.qr_png_data_url;
    a.download = `hub-qr-${hubDialog.data.hub_slug || 'outlet'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };



  const viewPlanningData = async (outlet) => {
    setPlanningDialog({ open: true, outlet, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/admin/outlets/${outlet.id}/planning-data`, { headers: getHeaders() });
      setPlanningDialog(p => ({ ...p, data: res.data, loading: false }));
    } catch {
      toast.error('Failed to load planning data');
      setPlanningDialog(p => ({ ...p, loading: false }));
    }
  };

  const deletePlanningData = async (outletId) => {
    try {
      const res = await axios.delete(`${API}/admin/outlets/${outletId}/planning-data`, { headers: getHeaders() });
      toast.success(`Deleted ${res.data.deleted} planning records`);
      setPlanningDialog({ open: false, outlet: null, data: null, loading: false });
      load();
    } catch { toast.error('Failed to delete planning data'); }
  };

  const handleBudgetUpload = async (outlet, file) => {
    if (!file) return;
    setBudgetUploading(outlet.id);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/pnl/yearly-budget/${outlet.id}?year=${budgetYear}`, form, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data || {};
      const tv = d.template_version === 'v2_codes' ? 'v2 codes' : 'legacy';
      toast.success(`Budget uploaded: ${d.items_count} line items for ${budgetYear} (${tv})`);
      // ITER234 — surface unmapped lines and skipped calc rows to admin
      if ((d.unmapped_lines || []).length > 0) {
        const sample = d.unmapped_lines.slice(0, 5)
          .map(u => `• ${u.line_item}${u.code ? ` [${u.code}]` : ''}`).join('\n');
        toast.warning(
          `${d.unmapped_lines.length} line(s) NOT mapped — excluded from totals (not booked as a cost):\n${sample}`,
          { duration: 12000 }
        );
      }
      if ((d.skipped_calc_rows || 0) > 0) {
        toast.info(`Skipped ${d.skipped_calc_rows} subtotal/total row(s) — system recomputes these.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload budget');
    } finally { setBudgetUploading(''); }
  };

  const viewBudgetData = async (outlet) => {
    setBudgetDialog({ open: true, outlet, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/admin/pnl/yearly-budget/${outlet.id}?year=${budgetYear}`, { headers: getHeaders() });
      setBudgetDialog(p => ({ ...p, data: res.data, loading: false }));
    } catch {
      toast.error('Failed to load budget');
      setBudgetDialog(p => ({ ...p, loading: false }));
    }
  };

  const deleteBudgetData = async (outletId) => {
    try {
      await axios.delete(`${API}/admin/pnl/yearly-budget/${outletId}?year=${budgetYear}`, { headers: getHeaders() });
      toast.success('Budget deleted');
      setBudgetDialog({ open: false, outlet: null, data: null, loading: false });
    } catch { toast.error('Failed to delete budget'); }
  };

  const downloadBudgetSample = async () => {
    try {
      const res = await axios.get(`${API}/admin/pnl/yearly-budget/sample/download`, {
        headers: getHeaders(), responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Yearly_Budget_Sample_v2.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download sample'); }
  };

  const openRateConfig = async (outlet) => {
    setRateDialog({ open: true, outlet, loading: true, meters: [] });
    try {
      const res = await axios.get(`${API}/admin/outlets/${outlet.id}/meters`, { headers: getHeaders() });
      setRateDialog(p => ({ ...p, loading: false, meters: res.data.meters || [] }));
    } catch {
      toast.error('Failed to load meters');
      setRateDialog(p => ({ ...p, loading: false, meters: [] }));
    }
  };

  const reloadMeters = async (outletId) => {
    try {
      const res = await axios.get(`${API}/admin/outlets/${outletId}/meters`, { headers: getHeaders() });
      setRateDialog(p => ({ ...p, meters: res.data.meters || [] }));
    } catch { /* silent */ }
  };

  const [meterActionForm, setMeterActionForm] = useState({
    open: false, mode: '', outletId: '', meterId: '',
    values: {}
  });

  const openMeterAction = (mode, outletId, meter = null) => {
    const today = new Date().toISOString().slice(0, 10);
    const initial = mode === 'add'
      ? { meter_type: 'electricity', name: '', meter_number: '', unit: 'kWh',
          reading_mode: 'opening_closing', unit_price: '', active_from: today, initial_reading: '' }
      : mode === 'edit'
      ? { name: meter.name || '', meter_number: meter.meter_number || '' }
      : mode === 'change-rate'
      ? { unit_price: '', effective_from: today }
      : mode === 'pause'
      ? { effective_from: today }
      : mode === 'resume'
      ? { effective_from: today }
      : mode === 'retire'
      ? { effective_from: today }
      : mode === 'replace'
      ? { name: '', meter_number: '', unit: meter?.unit || 'kWh',
          reading_mode: meter?.reading_mode || 'opening_closing', unit_price: '',
          active_from: today, initial_reading: '' }
      : {};
    setMeterActionForm({ open: true, mode, outletId, meterId: meter?.id || '',
                          values: initial, saving: false });
  };

  const submitMeterAction = async () => {
    const { mode, outletId, meterId, values } = meterActionForm;
    setMeterActionForm(p => ({ ...p, saving: true }));
    try {
      let url; let payload = { ...values };
      if (mode === 'add') url = `${API}/admin/outlets/${outletId}/meters`;
      else if (mode === 'edit') url = `${API}/admin/outlets/${outletId}/meters/${meterId}`;
      else if (mode === 'change-rate') url = `${API}/admin/outlets/${outletId}/meters/${meterId}/change-rate`;
      else if (mode === 'pause') url = `${API}/admin/outlets/${outletId}/meters/${meterId}/pause`;
      else if (mode === 'resume') url = `${API}/admin/outlets/${outletId}/meters/${meterId}/resume`;
      else if (mode === 'retire') url = `${API}/admin/outlets/${outletId}/meters/${meterId}/retire`;
      else if (mode === 'replace') url = `${API}/admin/outlets/${outletId}/meters/${meterId}/replace`;

      // Numeric coercions
      if (payload.unit_price !== undefined && payload.unit_price !== '') payload.unit_price = parseFloat(payload.unit_price);
      if (payload.initial_reading !== undefined && payload.initial_reading !== '') payload.initial_reading = parseFloat(payload.initial_reading);
      if (payload.initial_reading === '') delete payload.initial_reading;

      if (mode === 'edit') await axios.patch(url, payload, { headers: getHeaders() });
      else await axios.post(url, payload, { headers: getHeaders() });

      toast.success(`Meter ${mode === 'add' ? 'added' : mode + 'd'}`);
      setMeterActionForm({ open: false, mode: '', outletId: '', meterId: '', values: {} });
      await reloadMeters(outletId);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${mode}`);
    } finally {
      setMeterActionForm(p => ({ ...p, saving: false }));
    }
  };

  const saveRateConfig = async () => { /* legacy — retained for compat but no longer called */ };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/planning-template`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'planning_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  return (
    <div data-testid="admin-outlets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421]">Outlets</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Manage all restaurant outlets</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate}
            className="border-[#E2E8DF] text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]"
            data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#5C6B64]">Budget Year:</Label>
            <select value={budgetYear} onChange={e => setBudgetYear(Number(e.target.value))}
              className="h-8 px-2 text-xs border border-[#E2E8DF] rounded-md bg-white text-[#1A2421]"
              data-testid="budget-year-select">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={downloadBudgetSample}
              className="h-8 text-xs border-[#E2E8DF] text-[#5C6B64] hover:text-[color:var(--color-primary)]"
              data-testid="download-budget-sample">
              <Download className="w-3.5 h-3.5 mr-1" /> Sample
            </Button>
          </div>
          {(() => {
            const saturated = buStatus && buStatus.licensing_enabled
              && (buStatus.available ?? 0) <= 0;
            if (saturated) {
              return (
                <Button onClick={() => setActivateOpen(true)}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="add-business-unit-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Business Unit
                </Button>
              );
            }
            return (
              <Button onClick={openAdd}
                      className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                      data-testid="add-outlet-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Outlet
              </Button>
            );
          })()}
        </div>
      </div>

      {buStatus && buStatus.licensing_enabled && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
             data-testid="bu-status-banner">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
            Business Units
          </div>
          <div className="text-sm text-amber-900">
            <span data-testid="bu-used-count" className="font-semibold">{buStatus.used}</span>
            {' of '}
            <span data-testid="bu-limit-count" className="font-semibold">{buStatus.licensed_limit}</span>
            {' used'}
          </div>
          {(buStatus.available ?? 0) <= 0 && (
            <span className="ml-auto text-[11px] text-amber-800">
              Additional Business Unit required to add another outlet.
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-[#1A2421]">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <th className="px-4 py-3 font-medium">Outlet Name</th>
                    <th className="px-4 py-3 font-medium">Logo</th>
                    <th className="px-4 py-3 font-medium">City</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Portfolio</th>
                    <th className="px-4 py-3 font-medium">Budget/LY Data</th>
                    <th className="px-4 py-3 font-medium">P&L Budget</th>
                    <th className="px-4 py-3 font-medium">Utility Rates</th>
                    <th className="px-4 py-3 font-medium">Rooms</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-center">Guest Hub</th>
                    <th className="px-4 py-3 font-medium text-center">Digital Menu</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {outlets.map((o, i) => (
                    <tr key={o.id} className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                      data-testid={`outlet-row-${o.id}`}>
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#5C6B64] flex-shrink-0" strokeWidth={1.5} />
                          {o.name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {o.has_logo ? (
                            <div className="flex items-center gap-1.5">
                              <img src={`${API}/outlets/${o.id}/logo?t=${Date.now()}`} alt="logo" className="w-8 h-8 rounded object-cover border border-[#E2E8DF]" />
                              <Button variant="ghost" size="sm" onClick={() => handleLogoDelete(o)} className="text-[#C45A41] hover:text-[#C45A41] h-6 w-6 p-0" data-testid={`delete-logo-${o.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <label className="cursor-pointer flex items-center gap-1 text-xs text-[#5C6B64] hover:text-[color:var(--color-primary)]">
                              <Upload className="w-3.5 h-3.5" />
                              {uploading === o.id ? 'Uploading...' : 'Upload'}
                              <input type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(o, e.target.files[0])} data-testid={`upload-logo-${o.id}`} />
                            </label>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#5C6B64]">{o.city || '—'}</td>
                      <td className="px-4 py-3 text-[#5C6B64] capitalize">{(o.type || '').replace(/_/g, ' ') || '—'}</td>
                      <td className="px-4 py-3">
                        {o.portfolio_name ? (
                          <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64] text-[11px]" data-testid={`outlet-portfolio-${o.id}`}>
                            <FolderOpen className="w-2.5 h-2.5 mr-0.5" /> {o.portfolio_name}
                          </Badge>
                        ) : <span className="text-[#5C6B64] text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <label className="cursor-pointer" data-testid={`upload-excel-${o.id}`}>
                            <div className="flex items-center gap-1 text-xs text-[#5C6B64] hover:text-[color:var(--color-primary)] cursor-pointer">
                              <FileSpreadsheet className="w-3.5 h-3.5" />
                              {excelUploading === o.id ? 'Uploading...' : 'Upload'}
                            </div>
                            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleExcelUpload(o, e.target.files[0])} />
                          </label>
                          <Button variant="ghost" size="sm" onClick={() => viewPlanningData(o)}
                            className="text-[#5C6B64] hover:text-[color:var(--color-primary)] h-6 w-6 p-0"
                            data-testid={`view-planning-${o.id}`}>
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <label className="cursor-pointer" data-testid={`upload-budget-${o.id}`}>
                            <div className="flex items-center gap-1 text-xs text-[#5C6B64] hover:text-[color:var(--color-primary)] cursor-pointer">
                              <FileSpreadsheet className="w-3.5 h-3.5" />
                              {budgetUploading === o.id ? 'Uploading...' : 'Upload'}
                            </div>
                            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleBudgetUpload(o, e.target.files[0])} />
                          </label>
                          <Button variant="ghost" size="sm" onClick={() => viewBudgetData(o)}
                            className="text-[#5C6B64] hover:text-[color:var(--color-primary)] h-6 w-6 p-0"
                            data-testid={`view-budget-${o.id}`}>
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => openRateConfig(o)}
                          className="text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F3F5F2] h-7 text-xs"
                          data-testid={`utility-rates-${o.id}`}>
                          <Zap className="w-3.5 h-3.5 mr-1" /> Rates
                        </Button>
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => toggleRooms(o)}
                          className={`h-7 text-xs ${o.rooms_enabled
                            ? 'text-[#2D6A4F] hover:text-[#2D6A4F] hover:bg-[#2D6A4F]/5'
                            : 'text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F3F5F2]'}`}
                          data-testid={`toggle-rooms-${o.id}`}>
                          <BedDouble className="w-3.5 h-3.5 mr-1" />
                          {o.rooms_enabled ? 'ON' : 'OFF'}
                        </Button>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          className={o.status === 'active'
                            ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20'
                            : 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20'}
                          data-testid={`outlet-status-${o.id}`}
                        >
                          {o.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm"
                          onClick={() => openHubDialog(o)}
                          className="text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F3F5F2]"
                          data-testid={`hub-link-${o.id}`}
                          title="NFC Guest Hub link + QR">
                          <QrCode className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm"
                          onClick={() => navigate(`/admin/outlets/${o.id}/menus`)}
                          className="text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F3F5F2]"
                          data-testid={`digital-menu-${o.id}`}
                          title="Digital Menu">
                          <BookOpen className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(o)}
                            className="text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F3F5F2]"
                            data-testid={`edit-outlet-${o.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleStatus(o)}
                            className={o.status === 'active'
                              ? 'text-[#C45A41] hover:text-[#C45A41] hover:bg-[#C45A41]/5'
                              : 'text-[#2D6A4F] hover:text-[#2D6A4F] hover:bg-[#2D6A4F]/5'}
                            data-testid={`toggle-status-${o.id}`}>
                            <Power className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {outlets.length === 0 && (
                    <tr>
                      <td colSpan={13} className="px-4 py-10 text-center text-[#5C6B64]">No outlets found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Outlet Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col gap-0 p-0" data-testid="outlet-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-[#E2E8DF]/60">
            <DialogTitle className="text-[#1A2421]">{editing ? 'Edit Outlet' : 'Add New Outlet'}</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              {editing ? 'Update the outlet details below.' : 'Enter the details for the new outlet.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Outlet Name <span className="text-[#C45A41]">*</span></Label>
              <Input data-testid="outlet-name-input" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Downtown Bistro"
                className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">City</Label>
              <Input data-testid="outlet-city-input" value={form.city}
                onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                placeholder="e.g. Mumbai"
                className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Outlet Type</Label>
              <Select value={form.hierarchy} onValueChange={v => setForm(p => ({ ...p, hierarchy: v, parent_outlet_id: v === 'primary' ? '' : p.parent_outlet_id }))}>
                <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="outlet-hierarchy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary Outlet</SelectItem>
                  <SelectItem value="sub">Sub-Outlet (Cloud Kitchen / Co-located)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.hierarchy === 'sub' && (
              <div className="space-y-1.5" data-testid="outlet-parent-row">
                <Label className="text-sm text-[#5C6B64]">Parent Outlet</Label>
                <Select value={form.parent_outlet_id} onValueChange={v => setForm(p => ({ ...p, parent_outlet_id: v }))}>
                  <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="outlet-parent-select">
                    <SelectValue placeholder="Select parent outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    {outlets
                      .filter(o => o.hierarchy !== 'sub' && o.id !== editing?.id)
                      .map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2 py-1" data-testid="outlet-include-row">
              <input
                id="include_in_totals"
                type="checkbox"
                checked={!!form.include_in_totals}
                onChange={e => setForm(p => ({ ...p, include_in_totals: e.target.checked }))}
                className="h-4 w-4 accent-[var(--color-primary)] cursor-pointer"
                data-testid="outlet-include-in-totals-checkbox"
              />
              <Label htmlFor="include_in_totals" className="text-sm text-[#1A2421] cursor-pointer select-none">
                Include in Portfolio Totals
              </Label>
              <span className="text-[10px] text-[#5C6B64] ml-1">(Disable for sub-outlets whose revenue is already booked under a parent)</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Legal Entity Name <span className="text-[#C45A41]">*</span></Label>
              <Input data-testid="outlet-legal-entity-input" value={form.legal_entity_name}
                onChange={e => setForm(p => ({ ...p, legal_entity_name: e.target.value }))}
                placeholder="e.g. Woodland Hospitality Pvt Ltd"
                className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
              <p className="text-[10px] text-[#5C6B64]">Used on petty cash vouchers, invoices, and PDFs. Required before creating vouchers.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm text-[#5C6B64]">Petty Cash Max (₹)</Label>
                <Input data-testid="outlet-petty-cash-max-input" type="number" value={form.petty_cash_max_amount}
                  onChange={e => setForm(p => ({ ...p, petty_cash_max_amount: e.target.value }))}
                  placeholder="e.g. 5000"
                  className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-[#5C6B64]">Accounts Email</Label>
                <Input data-testid="outlet-accounts-email-input" type="email" value={form.accounts_email}
                  onChange={e => setForm(p => ({ ...p, accounts_email: e.target.value }))}
                  placeholder="accounts@..."
                  className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Accounts WhatsApp</Label>
              <Input data-testid="outlet-accounts-whatsapp-input" value={form.accounts_whatsapp}
                onChange={e => setForm(p => ({ ...p, accounts_whatsapp: e.target.value }))}
                placeholder="+91..."
                className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
              <p className="text-[10px] text-[#5C6B64]">Approved petty cash vouchers can be sent to this number directly via WhatsApp.</p>
            </div>
            <div className="space-y-1.5 border-t border-[#E2E8DF] pt-3">
              <Label className="text-sm font-medium text-[#1A2421]">POS Type</Label>
              <select
                value={form.pos_type || 'none'}
                onChange={e => setForm(p => ({ ...p, pos_type: e.target.value }))}
                className="w-full h-10 px-3 text-sm bg-white border border-[#1A2421]/15 hover:border-[#1A2421]/30 focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] rounded-md"
                data-testid="outlet-pos-type-select"
              >
                <option value="none">None — manual entry</option>
                <option value="winhms">WinHMS (importer active)</option>
                <option value="ids">IDS (placeholder — no adapter yet)</option>
                <option value="petpooja">PetPooja (placeholder — no adapter yet)</option>
                <option value="other">Other (placeholder — no adapter yet)</option>
              </select>
              <p className="text-[10px] text-[#5C6B64]">
                {form.pos_type === 'winhms'
                  ? 'Shows the "Import from WinHMS" button on the Daily Entry page. Triple-source reconciliation runs to the rupee before pre-fill.'
                  : (form.pos_type && form.pos_type !== 'none')
                    ? 'No adapter is built for this POS yet — the manager UI will not show an import button until one ships. Reserved for forward-compatibility.'
                    : 'Manager enters Daily Entry data manually. No import surface shown.'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Descriptor (optional)</Label>
              <Input data-testid="outlet-type-input" value={form.outlet_type}
                onChange={e => setForm(p => ({ ...p, outlet_type: e.target.value }))}
                placeholder="e.g. dine_in, premium, cafe"
                className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Portfolio</Label>
              <Select value={form.portfolio_id} onValueChange={v => setForm(p => ({ ...p, portfolio_id: v }))}>
                <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="outlet-portfolio-select">
                  <SelectValue placeholder="Select portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {portfolios.map(pf => (
                    <SelectItem key={pf.id} value={pf.id}>{pf.name}{pf.is_default ? ' (Default)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm text-[#5C6B64]">Target Food Cost %</Label>
                <Input
                  data-testid="outlet-target-fc-input"
                  type="number" step="0.1" min="0" max="100"
                  value={form.target_fc_pct}
                  onChange={e => setForm(p => ({ ...p, target_fc_pct: e.target.value }))}
                  placeholder="e.g. 35"
                  className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-[#5C6B64]">Target Beverage Cost %</Label>
                <Input
                  data-testid="outlet-target-bc-input"
                  type="number" step="0.1" min="0" max="100"
                  value={form.target_bc_pct}
                  onChange={e => setForm(p => ({ ...p, target_bc_pct: e.target.value }))}
                  placeholder="e.g. 30"
                  className="h-10 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]" />
              </div>
            </div>
            <p className="text-xs text-[#5C6B64] -mt-1">Used as the budget reference in 5-Minute Insights. Leave blank to disable.</p>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#E2E8DF] shrink-0 bg-white">
            <Button variant="outline" onClick={() => setDialogOpen(false)}
              className="border-[#E2E8DF] text-[#5C6B64]" data-testid="outlet-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="outlet-save-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Planning Data Viewer Dialog */}
      <Dialog open={planningDialog.open} onOpenChange={(o) => !o && setPlanningDialog({ open: false, outlet: null, data: null, loading: false })}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="planning-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">
              Planning Data — {planningDialog.outlet?.name}
            </DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Budget and Last Year Sales uploaded via Excel
            </DialogDescription>
          </DialogHeader>
          {planningDialog.loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : planningDialog.data?.count === 0 ? (
            <div className="text-center py-8 text-[#5C6B64]">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-[#E2E8DF]" />
              <p className="text-sm">No planning data uploaded yet</p>
              <p className="text-xs mt-1">Upload an Excel file with Date, Budget, Last Year Sales columns</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-[#5C6B64]">{planningDialog.data?.count} records</p>
                  <p className="text-xs text-[#5C6B64]">{planningDialog.data?.date_range}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => deletePlanningData(planningDialog.outlet?.id)}
                  className="text-[#C45A41] border-[#C45A41]/30 hover:bg-[#C45A41]/5"
                  data-testid="delete-planning-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete All
                </Button>
              </div>
              <div className="overflow-x-auto border border-[#E2E8DF] rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                      <th className="px-3 py-2 font-medium text-left">Date</th>
                      <th className="px-3 py-2 font-medium text-right">Budget</th>
                      <th className="px-3 py-2 font-medium text-right">LY Sales</th>
                      <th className="px-3 py-2 font-medium text-right">Room Budget</th>
                      <th className="px-3 py-2 font-medium text-right">Room LY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(planningDialog.data?.records || []).slice(0, 60).map((r, i) => (
                      <tr key={r.date} className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}>
                        <td className="px-3 py-1.5 text-[#1A2421]">{r.date}</td>
                        <td className="px-3 py-1.5 text-right text-[#1A2421]">{r.budget != null ? Number(r.budget).toLocaleString('en-IN') : '—'}</td>
                        <td className="px-3 py-1.5 text-right text-[#1A2421]">{r.last_year_sales != null ? Number(r.last_year_sales).toLocaleString('en-IN') : '—'}</td>
                        <td className="px-3 py-1.5 text-right text-[#1A2421]">{r.room_revenue_budget != null ? Number(r.room_revenue_budget).toLocaleString('en-IN') : '—'}</td>
                        <td className="px-3 py-1.5 text-right text-[#1A2421]">{r.room_revenue_last_year != null ? Number(r.room_revenue_last_year).toLocaleString('en-IN') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(planningDialog.data?.records || []).length > 60 && (
                  <p className="text-xs text-[#5C6B64] text-center py-2">Showing first 60 of {planningDialog.data?.count} records</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Utility Meters Management Dialog (Chunk 3) */}
      <Dialog open={rateDialog.open} onOpenChange={(o) => !o && setRateDialog({ open: false, outlet: null, loading: false, meters: [] })}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="utility-meters-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421] flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#D4A017]" /> Utility Meters — {rateDialog.outlet?.name}
            </DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Manage physical meters — add, edit, change rate, pause / resume, retire, or replace
            </DialogDescription>
          </DialogHeader>
          {rateDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {['electricity', 'gas', 'water'].map(mt => {
                const label = mt === 'electricity' ? 'Electricity'
                             : mt === 'gas' ? 'Gas / Fuel' : 'Water';
                const color = mt === 'electricity' ? '#D4A017'
                             : mt === 'gas' ? '#C45A41' : '#2D6A4F';
                const rows = (rateDialog.meters || []).filter(m => m.meter_type === mt);
                return (
                  <div key={mt} className="border border-[#E2E8DF] rounded-lg p-3 bg-[#F9F9F8]" data-testid={`meter-group-${mt}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-[#1A2421]" style={{ color }}>{label}</Label>
                      <Button size="sm" variant="outline"
                        onClick={() => openMeterAction('add', rateDialog.outlet.id)}
                        className="h-7 text-xs border-[#E2E8DF]"
                        data-testid={`add-${mt}-meter-btn`}>
                        <Plus className="w-3 h-3 mr-1" /> Add {label} Meter
                      </Button>
                    </div>
                    {rows.length === 0 ? (
                      <p className="text-xs text-[#5C6B64] italic">No meters configured</p>
                    ) : rows.map(m => {
                      const statusColor = m.status === 'active' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]'
                                        : m.status === 'paused' ? 'bg-[#D4A017]/10 text-[#D4A017]'
                                        : 'bg-[#5C6B64]/10 text-[#5C6B64]';
                      return (
                        <div key={m.id} className="bg-white border border-[#E2E8DF] rounded p-2 mb-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`meter-row-${m.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-[#1A2421]">{m.name}</span>
                              {m.meter_number && <span className="text-xs text-[#5C6B64]">{m.meter_number}</span>}
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase ${statusColor}`} data-testid={`meter-status-${m.id}`}>
                                {m.status}
                              </span>
                              {m.replaced_by_meter_id && <span className="text-[10px] text-[#5C6B64]">→ replaced</span>}
                            </div>
                            <p className="text-xs text-[#5C6B64] mt-0.5">
                              ₹{m.current_unit_price}/{m.unit} · {m.reading_mode === 'opening_closing' ? 'Opening/Closing' : 'Consumption'}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {m.status !== 'retired' && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMeterAction('edit', rateDialog.outlet.id, m)} data-testid={`edit-${m.id}-btn`}>Edit</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMeterAction('change-rate', rateDialog.outlet.id, m)} data-testid={`rate-${m.id}-btn`}>Change Rate</Button>
                                {m.status === 'active' && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMeterAction('pause', rateDialog.outlet.id, m)} data-testid={`pause-${m.id}-btn`}>Pause</Button>
                                )}
                                {m.status === 'paused' && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMeterAction('resume', rateDialog.outlet.id, m)} data-testid={`resume-${m.id}-btn`}>Resume</Button>
                                )}
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMeterAction('retire', rateDialog.outlet.id, m)} data-testid={`retire-${m.id}-btn`}>Retire</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMeterAction('replace', rateDialog.outlet.id, m)} data-testid={`replace-${m.id}-btn`}>Replace</Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialog({ open: false, outlet: null, loading: false, meters: [] })}
              className="border-[#E2E8DF] text-[#5C6B64]" data-testid="rate-cancel-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meter Action Dialog (Add / Edit / Change Rate / Pause / Resume / Retire / Replace) */}
      <Dialog open={meterActionForm.open} onOpenChange={(o) => !o && setMeterActionForm({ open: false, mode: '', outletId: '', meterId: '', values: {} })}>
        <DialogContent className="sm:max-w-lg" data-testid="meter-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421] capitalize">
              {meterActionForm.mode.replace('-', ' ')} Meter
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {(meterActionForm.mode === 'add' || meterActionForm.mode === 'replace') && (
              <>
                {meterActionForm.mode === 'add' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#5C6B64]">Utility Type</Label>
                      <select className="h-10 w-full border rounded px-2 border-[#E2E8DF] bg-white"
                        value={meterActionForm.values.meter_type || 'electricity'}
                        onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, meter_type: e.target.value, unit: e.target.value === 'electricity' ? 'kWh' : e.target.value === 'gas' ? 'm³' : 'kL' } }))}
                        data-testid="meter-type-select">
                        <option value="electricity">Electricity</option>
                        <option value="gas">Gas / Fuel</option>
                        <option value="water">Water</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6B64]">Reading Mode</Label>
                      <select className="h-10 w-full border rounded px-2 border-[#E2E8DF] bg-white"
                        value={meterActionForm.values.reading_mode || 'opening_closing'}
                        onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, reading_mode: e.target.value } }))}
                        data-testid="meter-mode-select">
                        <option value="opening_closing">Opening / Closing</option>
                        <option value="consumption_only">Consumption Only</option>
                      </select>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#5C6B64]">Meter Name</Label>
                    <Input value={meterActionForm.values.name || ''}
                      onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, name: e.target.value } }))}
                      data-testid="meter-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5C6B64]">Meter / Account Number</Label>
                    <Input value={meterActionForm.values.meter_number || ''}
                      onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, meter_number: e.target.value } }))}
                      data-testid="meter-number-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5C6B64]">Unit</Label>
                    <Input value={meterActionForm.values.unit || ''}
                      onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, unit: e.target.value } }))}
                      data-testid="meter-unit-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5C6B64]">Unit Price (₹)</Label>
                    <Input type="number" step="any" value={meterActionForm.values.unit_price || ''}
                      onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, unit_price: e.target.value } }))}
                      data-testid="meter-price-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5C6B64]">{meterActionForm.mode === 'replace' ? 'Replacement / Active From' : 'Active From'}</Label>
                    <Input type="date" value={meterActionForm.values.active_from || ''}
                      onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, active_from: e.target.value } }))}
                      data-testid="meter-active-from-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5C6B64]">Initial Reading (optional)</Label>
                    <Input type="number" step="any" value={meterActionForm.values.initial_reading || ''}
                      onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, initial_reading: e.target.value } }))}
                      data-testid="meter-initial-reading-input" />
                  </div>
                </div>
              </>
            )}
            {meterActionForm.mode === 'edit' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#5C6B64]">Meter Name</Label>
                  <Input value={meterActionForm.values.name || ''}
                    onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, name: e.target.value } }))}
                    data-testid="meter-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#5C6B64]">Meter / Account Number</Label>
                  <Input value={meterActionForm.values.meter_number || ''}
                    onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, meter_number: e.target.value } }))}
                    data-testid="meter-number-input" />
                </div>
              </div>
            )}
            {meterActionForm.mode === 'change-rate' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#5C6B64]">New Unit Price (₹)</Label>
                  <Input type="number" step="any" value={meterActionForm.values.unit_price || ''}
                    onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, unit_price: e.target.value } }))}
                    data-testid="rate-new-price-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#5C6B64]">Effective From</Label>
                  <Input type="date" value={meterActionForm.values.effective_from || ''}
                    onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, effective_from: e.target.value } }))}
                    data-testid="rate-effective-from-input" />
                </div>
              </div>
            )}
            {(meterActionForm.mode === 'pause' || meterActionForm.mode === 'resume' || meterActionForm.mode === 'retire') && (
              <div>
                <Label className="text-xs text-[#5C6B64]">Effective Date</Label>
                <Input type="date" value={meterActionForm.values.effective_from || ''}
                  onChange={e => setMeterActionForm(p => ({ ...p, values: { ...p.values, effective_from: e.target.value } }))}
                  data-testid="lifecycle-effective-from-input" />
                {meterActionForm.mode === 'retire' && (
                  <p className="text-xs text-[#C45A41] mt-2">This will permanently retire the meter. Historical readings will remain.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeterActionForm({ open: false, mode: '', outletId: '', meterId: '', values: {} })}
              className="border-[#E2E8DF] text-[#5C6B64]" data-testid="meter-action-cancel-btn">Cancel</Button>
            <Button onClick={submitMeterAction} disabled={meterActionForm.saving}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
              data-testid="meter-action-submit-btn">
              {meterActionForm.saving ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P&L Yearly Budget Dialog */}
      <Dialog open={budgetDialog.open} onOpenChange={(o) => !o && setBudgetDialog({ open: false, outlet: null, data: null, loading: false })}>
        <DialogContent className="sm:max-w-lg" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421] flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#2D6A4F]" /> P&L Budget — {budgetDialog.outlet?.name}
            </DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Yearly P&L budget for {budgetYear}
            </DialogDescription>
          </DialogHeader>
          {budgetDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : budgetDialog.data?.items?.length > 0 ? (
            <>
              <div className="text-[10px] text-[#5C6B64] mb-1">
                Uploaded: {budgetDialog.data.uploaded_at ? fmtDateIST(budgetDialog.data.uploaded_at) : '—'} by {budgetDialog.data.uploaded_by || '—'}
              </div>
              <div className="border border-[#E2E8DF] rounded-lg overflow-auto max-h-[340px]">
                <table className="w-full text-xs">
                  <thead className="bg-[#F3F5F2] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[#5C6B64] font-medium">Line Item</th>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <th key={m} className="px-2 py-2 text-right text-[#5C6B64] font-medium">{m}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {budgetDialog.data.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-[#E2E8DF]">
                        <td className="px-3 py-1.5 text-[#1A2421] font-medium whitespace-nowrap">{it.line_item}</td>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                          <td key={m} className="px-2 py-1.5 text-right text-[#5C6B64]">
                            {(it.monthly?.[String(m)] || 0).toLocaleString()}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-2">
                <Button variant="outline" size="sm" onClick={() => deleteBudgetData(budgetDialog.outlet?.id)}
                  className="text-[#C45A41] border-[#C45A41]/30 hover:bg-[#C45A41]/5 text-xs"
                  data-testid="delete-budget-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete Budget
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-sm text-[#5C6B64]">
              No budget uploaded for {budgetYear}.
              <p className="text-xs mt-1 text-[#5C6B64]/70">Upload any P&L Excel — first column = line items, remaining columns = months</p>
              <Button variant="link" onClick={downloadBudgetSample} className="text-[#2D6A4F] text-xs mt-1" data-testid="download-sample-btn">
                <Download className="w-3 h-3 mr-1" /> Download reference template
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NFC Guest Hub — shareable link + QR */}
      <Dialog open={hubDialog.open} onOpenChange={(o) => !o && setHubDialog({ open: false, outlet: null, data: null, loading: false })}>
        <DialogContent className="max-w-md" data-testid="hub-link-dialog">
          <DialogHeader>
            <DialogTitle>NFC Guest Hub — {hubDialog.outlet?.name}</DialogTitle>
            <DialogDescription>
              Encode this URL on the outlet&apos;s NFC tag or table tent. Guests land on the hub and
              can view the menu, explore membership or share feedback.
            </DialogDescription>
          </DialogHeader>
          {hubDialog.loading ? (
            <div className="py-8 text-center text-sm text-[#5C6B64]">Preparing…</div>
          ) : hubDialog.data ? (
            <div className="space-y-4">
              {/* URL row */}
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#5C6B64]">Hub URL</Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    readOnly
                    value={hubDialog.data.url}
                    className="text-xs font-mono"
                    data-testid="hub-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={copyHubLink}
                    data-testid="hub-copy-link"
                    title="Copy to clipboard"
                  >
                    {hubCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* QR image */}
              <div className="flex flex-col items-center bg-[#F3F5F2] rounded-md py-4">
                <img
                  src={hubDialog.data.qr_png_data_url}
                  alt={`QR for ${hubDialog.outlet?.name} hub`}
                  className="w-48 h-48"
                  data-testid="hub-qr-image"
                />
                <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] mt-2">
                  Slug: {hubDialog.data.hub_slug}
                </div>
              </div>

              {/* Feedback form selection */}
              <div data-testid="hub-feedback-selector">
                <Label className="text-xs uppercase tracking-wide text-[#5C6B64]">
                  Feedback card
                </Label>
                <div className="mt-1">
                  <Select
                    value={hubFeedback.selected || '__none__'}
                    onValueChange={(v) => saveHubFeedback(v === '__none__' ? null : v)}
                    disabled={hubFeedback.saving}
                  >
                    <SelectTrigger data-testid="hub-feedback-select">
                      <SelectValue placeholder="Not linked — feedback card hidden" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not linked — hide feedback card</SelectItem>
                      {hubFeedback.options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-[#5C6B64] mt-1.5 leading-relaxed"
                   data-testid="hub-feedback-help">
                  {hubFeedback.selected
                    ? `Card links guests to /feedback/${hubDialog.outlet?.id}/${hubFeedback.selected}.`
                    : 'No form linked yet — the feedback card is hidden on the public hub. Pick a form type above to enable it.'}
                </p>
              </div>

              {/* POS demo link — a shareable URL to paste into an email
                  to a prospect. NOT a nav destination for the platform. */}
              <div data-testid="pos-demo-link">
                <Label className="text-xs uppercase tracking-wide text-[#5C6B64]">
                  POS demo link
                </Label>
                <div className="mt-1 flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/pos-demo`}
                    className="text-xs font-mono"
                    data-testid="pos-demo-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/pos-demo`);
                      toast.success('POS demo link copied');
                    }}
                    data-testid="pos-demo-copy"
                    title="Copy to clipboard">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-[#5C6B64] mt-1.5 leading-relaxed">
                  Public sales surface — no login, pre-seeded data. Paste into an email to a lead.
                </p>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline" size="sm"
                  onClick={() => window.open(hubDialog.data.url, '_blank')}
                  data-testid="hub-preview"
                >
                  Preview
                </Button>
                <Button
                  size="sm" onClick={downloadHubQr}
                  data-testid="hub-download-qr"
                >
                  <Download className="w-4 h-4 mr-1.5" /> Download QR
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-[#C45A41]">
              Could not load hub link.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activate Business Unit modal */}
      <Dialog open={activateOpen} onOpenChange={(o) => { if (!o) { setActivateOpen(false); setActivateCode(''); } }}>
        <DialogContent data-testid="activate-bu-dialog">
          <DialogHeader>
            <DialogTitle>Additional Business Unit Required</DialogTitle>
            <DialogDescription>
              {buStatus?.licensing_enabled
                ? `Your current plan includes ${buStatus.licensed_limit} Business Unit${buStatus.licensed_limit === 1 ? '' : 's'}, and all available units are in use.`
                : 'Enter your activation code to add a Business Unit.'}
              {' '}
              To add another outlet, activate an additional Business Unit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <label className="text-xs uppercase tracking-widest text-[#5C6B64] font-semibold">
              Activation Code
            </label>
            <input value={activateCode}
                   onChange={e => setActivateCode(e.target.value)}
                   placeholder="RP-BU-XXXX-XXXX-XXXX"
                   className="w-full font-mono uppercase tracking-wider text-sm border border-[#E2E8DF] rounded-md p-2 focus:outline-none focus:border-amber-500"
                   data-testid="activate-bu-code-input" />
            <div className="text-[11px] text-[#5C6B64]">
              Need a code? Contact RestroPulse to add capacity.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setActivateOpen(false); setActivateCode(''); }}>Cancel</Button>
            <Button onClick={redeemActivationCode} disabled={activateBusy}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="activate-bu-submit">
              {activateBusy ? 'Activating…' : 'Activate Business Unit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
